import { Controller } from "@hotwired/stimulus"

// object-picker — a search-by-name-and-tag combobox that ENHANCES a native <select>.
//
// The select stays the form field (its name/value submit, and the wizard's live preview
// already listens for its bubbling `change`). On connect we read each <option>'s value,
// label, optgroup category and `data-tags`, hide the select, and render a trigger + a
// searchable panel. Choosing a result sets the select's value and dispatches `change`.
// Without JS the native grouped select still works.
export default class extends Controller {
  static targets = ["select"]

  connect() {
    this.options = this.readOptions()
    this.buildUI()
    this.syncTriggerLabel()
    this.onDocClick = (e) => { if (!this.element.contains(e.target)) this.close() }
    document.addEventListener("click", this.onDocClick)
  }

  disconnect() {
    document.removeEventListener("click", this.onDocClick)
  }

  // [{ value, label, category, tags, haystack }]
  readOptions() {
    return Array.from(this.selectTarget.options).map((opt) => {
      const category = opt.closest("optgroup")?.label || ""
      const tags = opt.dataset.tags || ""
      return {
        value: opt.value,
        label: opt.textContent.trim(),
        category,
        tags,
        haystack: `${opt.textContent} ${opt.value} ${category} ${tags}`.toLowerCase(),
      }
    })
  }

  buildUI() {
    this.selectTarget.classList.add("dst-object-picker__select--hidden")

    this.trigger = el("button", "dst-objpick__trigger", { type: "button" })
    this.trigger.addEventListener("click", (e) => { e.preventDefault(); this.toggle() })

    this.search = el("input", "dst-objpick__search", { type: "text", placeholder: "Search objects or tags…" })
    this.search.addEventListener("input", () => this.filter())
    this.search.addEventListener("keydown", (e) => this.onSearchKeydown(e))

    this.list = el("ul", "dst-objpick__list")
    this.itemEls = this.options.map((o) => {
      const btn = el("button", "dst-objpick__item", { type: "button" })
      btn.dataset.value = o.value
      btn.innerHTML = `<span class="dst-objpick__name"></span><span class="dst-objpick__cat"></span>`
      btn.querySelector(".dst-objpick__name").textContent = o.label
      btn.querySelector(".dst-objpick__cat").textContent = o.category
      btn.addEventListener("click", (e) => { e.preventDefault(); this.choose(o.value) })
      const li = el("li", "dst-objpick__li")
      li.appendChild(btn)
      this.list.appendChild(li)
      return { o, li }
    })

    this.empty = el("li", "dst-objpick__empty")
    this.empty.textContent = "No matching objects"
    this.empty.hidden = true
    this.list.appendChild(this.empty)

    this.panel = el("div", "dst-objpick__panel")
    this.panel.hidden = true
    this.panel.appendChild(this.search)
    this.panel.appendChild(this.list)

    this.box = el("div", "dst-objpick")
    this.box.appendChild(this.trigger)
    this.box.appendChild(this.panel)
    this.selectTarget.insertAdjacentElement("afterend", this.box)
  }

  toggle() { this.panel.hidden ? this.open() : this.close() }

  open() {
    this.panel.hidden = false
    this.box.classList.add("is-open")
    this.search.value = ""
    this.filter()
    this.search.focus()
  }

  close() {
    this.panel.hidden = true
    this.box.classList.remove("is-open")
  }

  filter() {
    const tokens = this.search.value.toLowerCase().split(/\s+/).filter(Boolean)
    let visible = 0
    this.itemEls.forEach(({ o, li }) => {
      const match = tokens.every((t) => o.haystack.includes(t))
      li.hidden = !match
      if (match) visible++
    })
    this.empty.hidden = visible > 0
  }

  onSearchKeydown(e) {
    if (e.key === "Escape") { e.preventDefault(); this.close(); this.trigger.focus() }
    if (e.key === "Enter") {
      e.preventDefault()
      const first = this.itemEls.find(({ li }) => !li.hidden)
      if (first) this.choose(first.o.value)
    }
  }

  choose(value) {
    this.selectTarget.value = value
    this.selectTarget.dispatchEvent(new Event("change", { bubbles: true }))
    this.syncTriggerLabel()
    this.close()
  }

  // keep the trigger label in step with the select (also if it changes elsewhere)
  onSelectChange() { this.syncTriggerLabel() }

  syncTriggerLabel() {
    const current = this.options.find((o) => o.value === this.selectTarget.value)
    this.trigger.textContent = current ? current.label : "Choose an object…"
  }
}

function el(tag, className, attrs = {}) {
  const node = document.createElement(tag)
  node.className = className
  Object.entries(attrs).forEach(([k, v]) => node.setAttribute(k, v))
  return node
}
